import { formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CATEGORY, CLASS_SCOPE_LABEL, MONTHS } from '../constants';
import type { CalendarEventItem } from '../types/calendar';

export interface UpcomingEventsProps {
  events: CalendarEventItem[];
  onSelect: (item: CalendarEventItem) => void;
}

/**
 * What is due next, soonest first.
 *
 * Measured from the real today rather than the period being browsed: an
 * administrator paging back to last year still wants to know what is coming
 * this week.
 */
export function UpcomingEvents({ events, onSelect }: UpcomingEventsProps) {
  return (
    <ul className="flex flex-col gap-2.5">
      {events.map((item) => {
        const style = CATEGORY[item.category];

        return (
          <li key={item.uuid}>
            <button
              type="button"
              onClick={() => onSelect(item)}
              className="flex w-full cursor-pointer items-start gap-3 rounded-2xl border border-line bg-surface-2 p-2.5 text-left transition-all hover:border-accent-line"
            >
              <span
                className={cn(
                  'grid h-11 w-11 shrink-0 place-items-center rounded-xl border leading-none',
                  style.chip,
                )}
                aria-hidden
              >
                <span className="text-base font-black">{item.startDate.slice(8, 10)}</span>
                <span className="text-[9px] font-black uppercase tracking-wider">
                  {MONTHS[Number(item.startDate.slice(5, 7)) - 1]?.slice(0, 3)}
                </span>
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-bold text-ink">
                  {item.title}
                </span>
                <span className="block truncate text-[11px] font-medium text-ink-3">
                  {CLASS_SCOPE_LABEL[item.classScope]}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[10.5px] font-semibold text-ink-3">
                    {formatTimeRange(item.startTime, item.endTime)}
                  </span>
                  <span
                    className={cn(
                      'rounded-full border px-1.5 py-px text-[9.5px] font-black uppercase tracking-wider',
                      style.chip,
                    )}
                  >
                    {style.label}
                  </span>
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
